import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";

import { updateSection } from "../../assignmentSlice";

import {
  convertCircledNumberToDigit,
  convertDigitToCircledNumber,
  getTextByLabel,
} from "../../../../utils/textUtils";
import { getStartingIndex } from "../../../../utils/resumeUtils";

import BasicChoicePage from "./basic_choice/BasicChoicePage";
import MultipleChoicePage from "./multiple_choice/MultipleChoicePage";
import DescriptiveConditionPage from "./descriptive_condition/DescriptiveConditionPage";
import SummaryBlankPage from "./summary/SummaryBlankPage";

const ParagraphPage = ({ setCurrentStep, isPreview, isReview }) => {
  const dispatch = useDispatch();

  const { section } = useSelector((state) => state.assignment);

  const [pageList, setPageList] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);

  const currentPage = pageList[currentIndex] || {
    type: "",
  };

  const handlePreviousPage = () => {
    if (currentIndex === 0) return;
    setCurrentIndex((prev) => prev - 1);
  };

  const handleNextPage = () => {
    if (currentIndex < pageList.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      const isAllAnswered = pageList.every((page) => page.isSubmitted);

      if (isAllAnswered) {
        if (!isPreview && !isReview) {
          dispatch(
            updateSection({
              sectionId: section.id,
              data: { is_finished: true, answer: pageList },
            })
          );
        }
        alert("학습이 완료되었습니다.");
        setCurrentStep(2);
      } else {
        alert("문제를 덜 풀었습니다.");
      }
    }
  };

  useEffect(() => {
    if (pageList.length > 0) return;

    if (!isPreview && section.answer.length > 0) {
      setPageList(section.answer);
      setCurrentIndex(getStartingIndex(section.resumeIndex));
      return;
    }

    setPageList(
      section.question.data.map((question, questionIndex) => {
        const newPage = {
          ...question,
          isCorrect: false,
          isSubmitted: false,
          answerData: question.answer,
        };
        if (
          [
              "title",
              "title_high",
              "title_kor",
              "title_kor_high",
              "topic",
              "topic_high",
              "topic_kor",
              "topic_kor_high",
              "match_eng",
              "match_eng_high",
              "match_kor",
              "match_kor_high",
              "mismatch_eng",
              "mismatch_eng_high",
              "mismatch_kor",
              "mismatch_kor_high",
              "blank",
              "blank_2",
              "blank_3",
          ].includes(newPage.type)
        ) {
            return {
              ...newPage,
              answer: convertCircledNumberToDigit(newPage.answer),
              choices: newPage.choices.map(
                (choice, choiceIndex) =>
                  `${convertDigitToCircledNumber(choiceIndex + 1)} ${choice}`
              ),
            };
        } else if (newPage.type === "summary_blank_AB") {
            // (A)(B) 단어쌍 선지: ["strain","response"] -> "① (A) strain · (B) response"
            return {
              ...newPage,
              answer: convertCircledNumberToDigit(newPage.answer),
              choices: newPage.choices.map((choice, choiceIndex) => {
                const pair = Array.isArray(choice) ? choice : [choice];
                const label = pair
                  .map(
                    (word, wordIndex) =>
                      `(${String.fromCharCode(65 + wordIndex)}) ${word}`
                  )
                  .join(" · ");
                return `${convertDigitToCircledNumber(
                  choiceIndex + 1
                )} ${label}`;
              }),
            };
        } else if (newPage.type === "order") {
            return {
              ...newPage,
              answer: convertCircledNumberToDigit(newPage.answer),
              choices: newPage.choices.map(
                (choice, choiceIndex) =>
                  `${convertDigitToCircledNumber(choiceIndex + 1)} ${choice}`
              ),
              text: (newPage.text ?? newPage.text1) + "\n\n" + newPage.text2,
            };
        } else if (newPage.type === "meaning_inference_eng") {
            return {
              ...newPage,
              answer: convertCircledNumberToDigit(newPage.answer),
              choices: newPage.choices.map(
                (choice, choiceIndex) =>
                  `${convertDigitToCircledNumber(choiceIndex + 1)} ${choice}`
              ),
              question: newPage.question.replace(/--(.*?)--/g, "<u>$1</u>"),
              text: newPage.text.replace(/--(.*?)--/g, "<u>$1</u>"),
            };
        } else if (
            [
              "flow",
              "flow_2",
              "flow_4",
              "vocabulary_low",
              "grammar_low",
            ].includes(newPage.type)
          ) {
            return {
              ...newPage,
              answer: convertCircledNumberToDigit(newPage.answer[0]),
              choices: newPage.choices.map(
                (choice) => `${choice} ${getTextByLabel(newPage.text, choice)}`
              ),
              text: newPage.text.replace(/--(.*?)--/g, "<u>$1</u>"),
            };
        } else if (
            [
              "vocabulary_mid",
              "vocabulary_high",
              "grammar_mid",
              "grammar_high",
            ].includes(newPage.type)
          ) {
            return {
              ...newPage,
              answer: convertCircledNumberToDigit(newPage.answer[0]),
              choices: newPage.choices.map(
                (choice, choiceIndex) =>
                  `${convertDigitToCircledNumber(choiceIndex + 1)} ${choice}`
              ),
              text: newPage.text.replace(/--(.*?)--/g, "<u>$1</u>"),
            };
        } else if (
            ["position", "position_1", "position_2"].includes(newPage.type)
          ) {
            return {
              ...newPage,
              answer: convertCircledNumberToDigit(newPage.answer),
              text: (
                (newPage.text ?? newPage.text1) +
                "\n\n" +
                newPage.text2
              ).replace(/--(.*?)--/g, "<u>$1</u>"),
            };
        } else if (
            [
              "match_count_eng",
              "match_count_eng_high",
              "match_count_kor",
              "match_count_kor_high",
            ].includes(newPage.type)
          ) {
            return {
              ...newPage,
              input: [],
              choices: (newPage.text2 ?? newPage.example)
                .split("\n")
                .map((choice) =>
                  choice.replace(/\((\d+)\)/g, (_, num) => {
                    const number = parseInt(num, 10);
                    return convertDigitToCircledNumber(number);
                  })
                ),
              answer: [...newPage.answer.matchAll(/\((\d+)\)/g)].map((match) =>
                Number(match[1])
              ),
            };
        } else if (
            [
              "descriptive_condition_low",
              "descriptive_condition_mid",
              "descriptive_condition_high",
              "descriptive_title",
              "descriptive_topic",
              "descriptive_summary",
            ].includes(newPage.type)
          ) {
            return {
              ...newPage,
              question: `${questionIndex + 1}. ${newPage.question.replace(
                /--(.*?)--/g,
                "<u>$1</u>"
              )}`,
              text: newPage.text.replace(/--(.*?)--/g, "<u>$1</u>"),
            };
        }
        return newPage;
      })
    );
    setCurrentIndex(0);
  }, [section]);

  if (
    [
      "title",
      "title_high",
      "title_kor",
      "title_kor_high",
      "topic",
      "topic_high",
      "topic_kor",
      "topic_kor_high",
      "match_eng",
      "match_eng_high",
      "match_kor",
      "match_kor_high",
      "mismatch_eng",
      "mismatch_eng_high",
      "mismatch_kor",
      "mismatch_kor_high",
      "blank",
      "blank_2",
      "blank_3",
      "order",
      "flow",
      "flow_2",
      "flow_4",
      "vocabulary_low",
      "vocabulary_mid",
      "vocabulary_high",
      "position",
      "position_1",
      "position_2",
      "meaning_inference_eng",
      "grammar_low",
      "grammar_mid",
      "grammar_high",
      "summary_blank_AB",
    ].includes(currentPage.type)
  ) {
    return (
      <BasicChoicePage
        setCurrentStep={setCurrentStep}
        isPreview={isPreview}
        isReview={isReview}
        section={section}
        currentPage={currentPage}
        currentIndex={currentIndex}
        pageList={pageList}
        setPageList={setPageList}
        handlePreviousPage={handlePreviousPage}
        handleNextPage={handleNextPage}
      />
    );
  } else if (
    [
      "match_count_eng",
      "match_count_eng_high",
      "match_count_kor",
      "match_count_kor_high",
    ].includes(currentPage.type)
  ) {
    return (
      <MultipleChoicePage
        setCurrentStep={setCurrentStep}
        isPreview={isPreview}
        isReview={isReview}
        section={section}
        currentPage={currentPage}
        currentIndex={currentIndex}
        pageList={pageList}
        setPageList={setPageList}
        handlePreviousPage={handlePreviousPage}
        handleNextPage={handleNextPage}
      />
    );
  } else if (currentPage.type === "summary_blank") {
    return (
      <SummaryBlankPage
        section={section}
        currentPage={currentPage}
        currentIndex={currentIndex}
        pageList={pageList}
        setPageList={setPageList}
        handlePreviousPage={handlePreviousPage}
        handleNextPage={handleNextPage}
      />
    );
  } else if (
    [
      "descriptive_condition_low",
      "descriptive_condition_mid",
      "descriptive_condition_high",
      "descriptive_title",
      "descriptive_topic",
      "descriptive_summary",
    ].includes(currentPage.type)
  ) {
    return (
      <DescriptiveConditionPage
        setCurrentStep={setCurrentStep}
        isPreview={isPreview}
        isReview={isReview}
        section={section}
        currentPage={currentPage}
        currentIndex={currentIndex}
        pageList={pageList}
        setPageList={setPageList}
        handlePreviousPage={handlePreviousPage}
        handleNextPage={handleNextPage}
      />
    );
  }

  return <div></div>;
};

export default ParagraphPage;
